import { FormControl, Stack, SxProps } from "@mui/material";
import { DatePicker, DatePickerProps, PickersShortcutsItem } from "@mui/x-date-pickers-pro";
import { SuggestedChip, SuggestedChipProps } from "components/ArtificialIntelligence/SuggestedChip";
import { SuggestedIcon } from "components/ArtificialIntelligence/SuggestedIcon";
import { DateShortcutChips } from "components/DateShortcutChips";
import { useValidatingFormContext } from "hooks/useValidatingForm";
import { DateTime } from "luxon";
import { useMemo } from "react";
import { Controller, FieldValues, UseControllerProps } from "react-hook-form";

import { DisplayOnlyField } from "./DisplayOnlyField";
import { ErrorDisplay } from "./ErrorDisplay";
import { TextWithTooltip } from "./TextWithTooltip";

export type DateFieldProps<T extends FieldValues> = {
  label: string;
  tooltip?: React.ReactNode;
  datePickerProps?: DatePickerProps<DateTime>;
  sx?: SxProps;
  disabled?: boolean;
  required?: boolean;
  hideErrors?: boolean;
  helperText?: string;
  endAdornment?: React.ReactNode;
  shortcuts?: PickersShortcutsItem<DateTime>[]; // optional date shortcuts rendered inside the picker
  clearable?: boolean; // whether the field can be cleared (defaults to !required)
  suggestionProps?: SuggestedChipProps & { showFullSuggestionChip?: boolean };
} & UseControllerProps<T>;

export function DateField<T extends FieldValues>(props: DateFieldProps<T>) {
  const labelWithTooltip = useMemo(
    () =>
      props.tooltip ? <TextWithTooltip text={props.label} tooltip={props.tooltip} /> : props.label,
    [props.label, props.tooltip]
  );
  // 72px accounts for tooltip (24px) + calendar icon (24px) + padding/spacing
  // 48px accounts for just calendar icon (24px) + padding/spacing
  const slotWidth = props.tooltip ? 72 : 48;
  const clearable = props.clearable ?? !props.required;

  const formContext = useValidatingFormContext();
  if (formContext?.metadata?.isDisplayOnly) {
    const value = formContext?.getValues(props.name) as string | undefined;
    const displayValue = value ? DateTime.fromISO(value).toFormat("MM/dd/yyyy") : "";
    return (
      <FormControl fullWidth sx={props.sx}>
        <DisplayOnlyField text={displayValue} label={labelWithTooltip} />
      </FormControl>
    );
  }

  return (
    <FormControl fullWidth sx={props.sx}>
      <Controller
        name={props.name}
        control={props.control}
        render={({ field: { onChange, value }, fieldState: { error } }) => {
          return (
            <>
              <DatePicker
                {...props.datePickerProps}
                label={labelWithTooltip}
                value={value ? DateTime.fromISO(value) : null}
                onChange={(newValue) => onChange(newValue?.toISODate() ?? null)}
                disabled={props.disabled}
                slots={{
                  inputAdornment: (adornmentProps) => (
                    <Stack direction="row" alignItems="center">
                      {props.suggestionProps && props.suggestionProps.showFullSuggestionChip ? (
                        <SuggestedChip {...props.suggestionProps} />
                      ) : (
                        <SuggestedIcon {...props.suggestionProps} />
                      )}
                      {props.endAdornment}
                      {adornmentProps.children}
                    </Stack>
                  ),
                  ...(props.shortcuts && props.shortcuts.length > 0
                    ? { shortcuts: DateShortcutChips }
                    : {}),
                }}
                slotProps={{
                  inputAdornment: {
                    ...props.datePickerProps?.slotProps?.inputAdornment,
                    // Only override the adornment position for the AI-suggestion
                    // chip (rendered at the start); otherwise leave MUI's default
                    // ("end"). Passing `position: undefined` is an invalid value
                    // that trips a React DOM warning and drops the calendar
                    // "Choose date" button, leaving a type-only field.
                    ...(props.suggestionProps ? { position: "start" as const } : {}),
                  },
                  ...props.datePickerProps?.slotProps,
                  ...(clearable ? { field: { clearable: true } } : {}),
                  textField: {
                    ...props.datePickerProps?.slotProps?.textField,
                    error: !!error,
                    required: props.required,
                    sx: {
                      "& .MuiInputLabel-shrink": {
                        width: "auto", // Full width when shrunk/floating
                      },
                      "& .MuiInputLabel-root:not(.MuiInputLabel-shrink)": {
                        width: `calc(100% - ${slotWidth}px)`, // Limit width to avoid overlap with tooltip/date picker icons
                      },
                      ...(props.datePickerProps?.slotProps?.textField &&
                      "sx" in props.datePickerProps.slotProps.textField
                        ? props.datePickerProps.slotProps.textField.sx
                        : {}),
                    },
                  },
                  actionBar: {
                    ...props.datePickerProps?.slotProps?.actionBar,
                    actions: clearable ? ["clear", "accept"] : ["accept"],
                  },
                  ...(props.shortcuts && props.shortcuts.length > 0
                    ? {
                        shortcuts: {
                          items: props.shortcuts,
                        },
                      }
                    : {}),
                }}
              />
              <ErrorDisplay
                error={error}
                helperText={props.helperText}
                hideErrors={props.hideErrors}
              />
            </>
          );
        }}
      />
    </FormControl>
  );
}
